
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import api from '../utils/api';
import SectionHeader from '../components/SectionHeader';
import { COMPANY_FACTS } from '../config/companyFacts';
import { sanitizePhone, isValidPhone, PHONE_ERROR, phoneInputProps } from '../utils/phone';

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/api$/,'') || '';

// Phone input wired into react-hook-form: digits only while typing,
// validated as a 10-digit Indian mobile on submit.
const phoneField = (register, name, required) => {
  const r = register(name, {
    required: required && 'Required',
    validate: v => (!required && !String(v || '').trim()) || isValidPhone(v) || PHONE_ERROR,
  });
  return {
    ...r,
    onChange: e => { e.target.value = sanitizePhone(e.target.value); return r.onChange(e); },
    ...phoneInputProps,
    placeholder: '10-digit mobile number',
  };
};
const imgSrc = (p) => !p ? '' : /^https?:\/\//i.test(p) ? p : `${API_BASE}${p}`;

export function About() {
  return (
    <>
      <Helmet>
        <title>About Wedoo Interiors | Interior Design Studio Tumakuru</title>
        <meta name="description" content="Learn about Wedoo Interiors — our story, values, and decade of crafting extraordinary living spaces in Tumakuru, Karnataka." />
      </Helmet>
      <div style={{ paddingTop:'7rem', paddingBottom:'5rem', background:'#141414', borderBottom:'1px solid rgba(245,168,0,0.12)', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', top:'-60px', right:'-60px', width:'300px', height:'300px', border:'1px solid rgba(245,168,0,0.06)', borderRadius:'50%' }} />
        <div className="container">
          <SectionHeader eyebrow="Our Story" title={<>About <em style={{color:'#F5A800',fontStyle:'italic'}}>Wedoo Interiors</em></>} subtitle={`${COMPANY_FACTS.years}+ years of turning spaces into timeless works of art — in Tumakuru and across Karnataka.`} />
        </div>
      </div>
      <section className="section-pad" style={{ background:'#0c0c0c' }}>
        <div className="container">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6rem', alignItems:'center', marginBottom:'6rem' }} className="about-split">
            <div>
              <h2 style={{ fontFamily:'var(--font-display)', fontSize:'2.2rem', fontWeight:600, color:'#f8f5ef', marginBottom:'1.5rem', lineHeight:1.2 }}>Who We Are</h2>
              <div className="gold-line" />
              <p style={{ color:'#8a8278', lineHeight:'1.9', marginBottom:'1.25rem', fontSize:'0.88rem' }}>
                Wedoo Interiors is Tumakuru's premier full-service interior design studio. We combine artistic vision with technical mastery to create spaces that reflect your personality, enhance your lifestyle, and stand the test of time.
              </p>
              <p style={{ color:'#8a8278', lineHeight:'1.9', marginBottom:'2.5rem', fontSize:'0.88rem' }}>
                From the first consultation to the final handover, our team of specialist designers and craftsmen work with complete transparency — delivering extraordinary results on time, every time.
              </p>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem', marginBottom:'2.5rem' }}>
                {[[COMPANY_FACTS.projects,'Projects Done'],[COMPANY_FACTS.yearsLabel,'Years Experience'],[COMPANY_FACTS.satisfaction,'Satisfaction'],[COMPANY_FACTS.warrantyLabel,'Warranty']].map(([v,l]) => (
                  <div key={l} style={{ padding:'1.25rem', background:'#141414', border:'1px solid rgba(245,168,0,0.15)', borderRadius:'var(--radius)' }}>
                    <div style={{ fontFamily:'var(--font-display)', fontSize:'2rem', color:'#F5A800', lineHeight:1 }}>{v}</div>
                    <div style={{ fontSize:'0.68rem', letterSpacing:'0.14em', textTransform:'uppercase', color:'#8a8278', marginTop:'0.35rem' }}>{l}</div>
                  </div>
                ))}
              </div>
              <Link to="/get-free-quote" className="btn btn-gold">Start Your Project</Link>
            </div>
            <img src="https://images.unsplash.com/photo-1618219908412-a29a1bb7b86e?w=700&q=85" alt="Wedoo Interiors Design Studio" style={{ width:'100%', height:'500px', objectFit:'cover' }} />
          </div>
          <SectionHeader eyebrow="Our Values" title={<>What <em style={{color:'#F5A800',fontStyle:'italic'}}>Drives Us</em></>} subtitle="Every decision we make is guided by four core principles." center />
          <div className="grid-4">
            {[
              { icon:'🎯', title:'Precision', desc:'Every measurement, material and finish chosen and installed with meticulous care.' },
              { icon:'💡', title:'Innovation', desc:'2030-forward design aesthetics powered by the latest materials and technology.' },
              { icon:'🤝', title:'Integrity', desc:'Complete price transparency. No hidden costs. No surprises. Ever.' },
              { icon:'🏆', title:'Excellence', desc:`${COMPANY_FACTS.warrantyYears}-year warranty backed by ${COMPANY_FACTS.projects} successfully delivered projects.` },
            ].map(v => (
              <div key={v.title} style={{ padding:'2.5rem 2rem', background:'#141414', border:'1px solid rgba(255,255,255,0.05)', borderRadius:'var(--radius)', textAlign:'center', transition:'all 0.4s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(245,168,0,0.25)'; e.currentTarget.style.transform='translateY(-6px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,255,255,0.05)'; e.currentTarget.style.transform='translateY(0)'; }}>
                <div style={{ fontSize:'2.2rem', marginBottom:'1.25rem' }}>{v.icon}</div>
                <h4 style={{ fontFamily:'var(--font-display)', fontSize:'1.25rem', fontWeight:400, color:'#f8f5ef', marginBottom:'0.75rem' }}>{v.title}</h4>
                <p style={{ color:'#8a8278', fontSize:'0.82rem', lineHeight:'1.7' }}>{v.desc}</p>
              </div>
            ))}
          </div>
          <div style={{ marginTop:'5rem', background:'#141414', border:'1px solid rgba(245,168,0,0.15)', borderRadius:'var(--radius)', padding:'3rem', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'3rem', alignItems:'center' }} className="addr-split">
            <div>
              <SectionHeader eyebrow="Find Us" title="Visit Our Studio" />
              <div style={{ display:'flex', flexDirection:'column', gap:'1.25rem' }}>
                {[
                  { icon:'📍', text:'#1, Near Muneshwara Temple, Manjunath Nagar, Gokula Extension, Tumakuru, Karnataka 572104' },
                  { icon:'📞', text:'+919686583195', href:'tel:+919686583195' },
                  { icon:'💬', text:'WhatsApp Us', href:'https://api.whatsapp.com/send?phone=919686583195' },
                  { icon:'🕒', text:'Mon – Sat: 9:00 AM – 7:00 PM' },
                ].map(i => (
                  <div key={i.icon} style={{ display:'flex', gap:'0.85rem', alignItems:'flex-start' }}>
                    <span style={{ color:'#F5A800', marginTop:'2px', width:'20px', flexShrink:0 }}>{i.icon}</span>
                    {i.href ? <a href={i.href} style={{ color:'#ede8de', fontSize:'0.85rem', lineHeight:'1.7' }}>{i.text}</a> : <p style={{ color:'#8a8278', fontSize:'0.85rem', lineHeight:'1.7' }}>{i.text}</p>}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ borderRadius:'var(--radius)', overflow:'hidden', border:'1px solid rgba(245,168,0,0.15)' }}>
              <iframe title="Wedoo Interiors" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15552.64!2d77.1173!3d13.3379!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTPCsDIwJzE2LjQiTiA3N8KwMDcnMDIuMyJF!5e0!3m2!1sen!2sin!4v1" width="100%" height="240" style={{ border:0, display:'block', filter:'grayscale(0.5) invert(0.9)' }} loading="lazy" />
            </div>
          </div>
        </div>
        <style>{`@media(max-width:860px){.about-split,.addr-split{grid-template-columns:1fr!important;gap:2.5rem!important}}`}</style>
      </section>
    </>
  );
}

// ─── SERVICES ─────────────────────────────────────────────────────────────────
export function Services() {
  const services = [
    { icon:'🗄️', title:'Complete Modular Works', path:'/services/complete-modular-site-work', desc:'Precision-crafted modular solutions and complete site execution from demolition to final handover.', features:['Kitchen & Wall Demolition','Floor Tile Installation','Electrical Works','New Wall Construction'] },
    { icon:'🛋️', title:'Interior Design', path:'/services/interior-design', desc:'Bespoke interiors combining aesthetics, function and your personality in every detail.', features:['Space Planning','3D Visualization','Material Selection','Furniture Curation'] },
    { icon:'💡', title:'Home Lighting', path:'/services/home-lighting', desc:'Expert lighting design that creates perfect ambiance for every room and occasion.', features:['Cove Lighting','Statement Chandeliers','Task Lighting','Smart Controls'] },
    { icon:'🍳', title:'Modular Kitchen', path:'/services/modular-kitchen', desc:'Smart, space-efficient kitchens with premium fittings and innovative storage.', features:['Custom Countertops','Premium Hardware','Smart Storage','Designer Backsplashes'] },
    { icon:'🎨', title:'False Ceiling & Painting', path:'/services/false-ceiling-painting', desc:'Creative ceiling designs and premium finishes that transform any space.', features:['Gypsum Ceilings','POP Work','Texture Paint','Cove with LED'] },
    { icon:'🪟', title:'Aluminium & Glass Works', path:'/services/aluminium-glass-works', desc:'Contemporary glass and aluminium solutions for modern homes.', features:['Shower Partitions','Glass Railings','Aluminium Doors','Spider Fittings'] },
  ];
  return (
    <>
      <Helmet>
        <title>Interior Design Services | Wedoo Interiors Tumakuru</title>
        <meta name="description" content="Wedoo Interiors offers complete interior design services in Tumakuru — modular kitchens, false ceilings, home lighting, aluminium & glass works. 5-year warranty." />
      </Helmet>
      <div style={{ paddingTop:'7rem', paddingBottom:'1rem', background:'#141414', borderBottom:'1px solid rgba(245,168,0,0.12)' }}>
        <div className="container"><SectionHeader eyebrow="What We Offer" title={<>Our <em style={{color:'#F5A800',fontStyle:'italic'}}>Services</em></>} subtitle="Complete interior solutions — concept to completion — backed by a 5-year warranty." /></div>
      </div>
      <section className="section-pad" style={{ background:'#0c0c0c' }}>
        <div className="container">
          <div style={{ display:'flex', flexDirection:'column', gap:'2px', background:'rgba(245,168,0,0.05)' }}>
            {services.map((s,i) => (
              <div key={s.title} style={{ display:'grid', gridTemplateColumns:'200px 1fr 1fr', gap:'0', background:'#0c0c0c', padding:'3rem', borderBottom:'1px solid rgba(255,255,255,0.04)', transition:'all 0.4s' }}
                onMouseEnter={e => { e.currentTarget.style.background='#141414'; }}
                onMouseLeave={e => { e.currentTarget.style.background='#0c0c0c'; }}
                className="svc-row">
                <div style={{ display:'flex', flexDirection:'column', justifyContent:'center', borderRight:'1px solid rgba(255,255,255,0.05)', paddingRight:'3rem' }}>
                  <div style={{ fontSize:'2.5rem', marginBottom:'0.75rem' }}>{s.icon}</div>
                  <span style={{ fontSize:'0.58rem', letterSpacing:'0.2em', textTransform:'uppercase', color:'#F5A800', fontWeight:500 }}>Service {String(i+1).padStart(2,'0')}</span>
                </div>
                <div style={{ padding:'0 3rem', borderRight:'1px solid rgba(255,255,255,0.05)' }}>
                  <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.5rem', fontWeight:400, color:'#f8f5ef', marginBottom:'1rem', lineHeight:1.2 }}>{s.title}</h3>
                  <p style={{ color:'#8a8278', fontSize:'0.84rem', lineHeight:'1.8', marginBottom:'1.5rem' }}>{s.desc}</p>
                  <Link to={s.path} className="btn btn-outline" style={{ padding:'0.6rem 1.4rem', fontSize:'0.65rem' }}>Learn More →</Link>
                </div>
                <div style={{ paddingLeft:'3rem', display:'flex', flexDirection:'column', justifyContent:'center' }}>
                  {s.features.map(f => (
                    <div key={f} style={{ display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.5rem 0', borderBottom:'1px solid rgba(255,255,255,0.04)', color:'#ede8de', fontSize:'0.82rem' }}>
                      <span style={{ width:'4px', height:'4px', background:'#F5A800', borderRadius:'50%', flexShrink:0 }} />{f}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <style>{`@media(max-width:768px){.svc-row{grid-template-columns:1fr!important;gap:1.5rem!important}}`}</style>
      </section>
    </>
  );
}

// ─── PORTFOLIO ────────────────────────────────────────────────────────────────
export function Portfolio() {
  const [projects, setProjects] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [cat, setCat] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const CATS = ['all','living-room','bedroom','kitchen','bathroom','false-ceiling','commercial','complete-modular'];

  useEffect(() => { api.get('/portfolio').then(r => { const d = Array.isArray(r.data) ? r.data : (r.data?.projects || r.data?.data || []); setProjects(d); setFiltered(d); }).catch(()=>{}).finally(()=>setLoading(false)); }, []);
  useEffect(() => { setFiltered(cat==='all' ? projects : projects.filter(p=>p.category===cat)); }, [cat,projects]);

  return (
    <>
      <Helmet>
        <title>Portfolio | Wedoo Interiors — Interior Design Projects Tumakuru</title>
        <meta name="description" content="Browse Wedoo Interiors' portfolio of stunning interior design projects — modular kitchens, living rooms, false ceilings and more in Tumakuru, Karnataka." />
      </Helmet>
      <div style={{ paddingTop:'7rem', paddingBottom:'5rem', background:'#141414', borderBottom:'1px solid rgba(245,168,0,0.12)' }}>
        <div className="container"><SectionHeader eyebrow="Our Work" title={<>Project <em style={{color:'#F5A800',fontStyle:'italic'}}>Portfolio</em></>} subtitle="A curated collection of our finest interior transformations across Karnataka." /></div>
      </div>
      <section className="section-pad" style={{ background:'#0c0c0c' }}>
        <div className="container">
          <div style={{ display:'flex', gap:'0.4rem', flexWrap:'wrap', marginBottom:'3rem' }}>
            {CATS.map(c => (
              <button key={c} onClick={()=>setCat(c)} style={{ padding:'0.5rem 1.25rem', fontSize:'0.68rem', letterSpacing:'0.1em', textTransform:'capitalize', cursor:'pointer', transition:'all 0.3s', background: c===cat ? '#F5A800' : 'rgba(255,255,255,0.04)', color: c===cat ? '#0c0c0c' : '#8a8278', border: c===cat ? 'none' : '1px solid rgba(255,255,255,0.08)', fontFamily:'var(--font-sans)', fontWeight:500 }}>
                {c.replace(/-/g,' ')}
              </button>
            ))}
          </div>
          {loading ? <div style={{ textAlign:'center', padding:'4rem', color:'#8a8278' }}>Loading projects...</div> :
           filtered.length===0 ? (
            <div style={{ textAlign:'center', padding:'5rem' }}>
              <div style={{ fontSize:'3rem', marginBottom:'1rem' }}>🏠</div>
              <p style={{ color:'#8a8278' }}>No projects yet in this category. Check back soon!</p>
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'20px' }} className="port-grid">
              {filtered.map((p,i) => (
                <div key={p._id} className="port-item" style={{ height:'340px', cursor:'pointer' }} onClick={()=>setSelected(p)}>
                  {p.images?.[0] ? <img src={imgSrc(p.images[0])} alt={p.title} /> : <div style={{ width:'100%', height:'100%', background:'#1c1c1c', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'3rem' }}>🏠</div>}
                  <div className="port-overlay">
                    <span className="badge badge-gold" style={{ marginBottom:'0.5rem', alignSelf:'flex-start' }}>{p.category?.replace(/-/g,' ')}</span>
                    <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.15rem', fontWeight:400, color:'#f8f5ef' }}>{p.title}</h3>
                    {p.location && <p style={{ color:'#8a8278', fontSize:'0.7rem', marginTop:'0.2rem' }}>📍 {p.location}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <style>{`@media(max-width:640px){.port-grid{grid-template-columns:1fr!important}}`}</style>
      </section>

      {/* Lightbox */}
      {selected && (
        <div onClick={()=>setSelected(null)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.96)', zIndex:9000, display:'flex', alignItems:'center', justifyContent:'center', padding:'1.5rem', overflowY:'auto' }}>
          <div onClick={e=>e.stopPropagation()} style={{ background:'#141414', border:'1px solid rgba(245,168,0,0.2)', borderRadius:'var(--radius)', maxWidth:'820px', width:'100%', maxHeight:'90vh', overflow:'auto' }}>
            {selected.images?.[0] && <img src={imgSrc(selected.images[0])} alt={selected.title} style={{ width:'100%', height:'420px', objectFit:'cover' }} />}
            <div style={{ padding:'2.5rem' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1rem' }}>
                <div>
                  <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.8rem', fontWeight:400, color:'#f8f5ef', marginBottom:'0.5rem' }}>{selected.title}</h2>
                  <span className="badge badge-gold">{selected.category?.replace(/-/g,' ')}</span>
                </div>
                <button onClick={()=>setSelected(null)} style={{ color:'#8a8278', fontSize:'1.3rem', cursor:'pointer', background:'none' }}>✕</button>
              </div>
              {selected.description && <p style={{ color:'#8a8278', lineHeight:'1.8', fontSize:'0.86rem', marginTop:'1rem' }}>{selected.description}</p>}
              <div style={{ display:'flex', gap:'0.75rem', flexWrap:'wrap', marginTop:'1rem' }}>
                {selected.bhkType && <span className="badge badge-muted">{selected.bhkType}</span>}
                {selected.location && <span className="badge badge-muted">📍 {selected.location}</span>}
                {selected.area && <span className="badge badge-muted">{selected.area}</span>}
              </div>
              {selected.images?.length > 1 && (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'4px', marginTop:'1.5rem' }}>
                  {selected.images.slice(1).map((img,i) => <img key={i} src={imgSrc(img)} alt="" style={{ width:'100%', height:'100px', objectFit:'cover' }} />)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── GALLERY ────────────────────────────────────────────────────────────────
export function Gallery() {
  const [projects, setProjects] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [cat, setCat] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const CATS = ['all','living-room','bedroom','kitchen','bathroom','false-ceiling','commercial','complete-modular'];

  useEffect(() => { api.get('/gallery').then(r => { const d = Array.isArray(r.data) ? r.data : (r.data?.projects || r.data?.data || []); setProjects(d); setFiltered(d); }).catch(()=>{}).finally(()=>setLoading(false)); }, []);
  useEffect(() => { setFiltered(cat==='all' ? projects : projects.filter(p=>p.category===cat)); }, [cat,projects]);

  return (
    <>
      <Helmet>
        <title>Gallery | Wedoo Interiors — Interior Design Projects Tumakuru</title>
        <meta name="description" content="Browse Wedoo Interiors' portfolio of stunning interior design projects — modular kitchens, living rooms, false ceilings and more in Tumakuru, Karnataka." />
      </Helmet>
      <div style={{ paddingTop:'7rem', paddingBottom:'5rem', background:'#141414', borderBottom:'1px solid rgba(245,168,0,0.12)' }}>
        <div className="container"><SectionHeader eyebrow="" title={<>Project <em style={{color:'#F5A800',fontStyle:'italic'}}>Gallery</em></>} subtitle="" /></div>
      </div>
      <section className="section-pad" style={{ background:'#0c0c0c' }}>
        <div className="container">
          <div style={{ display:'flex', gap:'0.4rem', flexWrap:'wrap', marginBottom:'3rem' }}>
            {CATS.map(c => (
              <button key={c} onClick={()=>setCat(c)} style={{ padding:'0.5rem 1.25rem', fontSize:'0.68rem', letterSpacing:'0.1em', textTransform:'capitalize', cursor:'pointer', transition:'all 0.3s', background: c===cat ? '#F5A800' : 'rgba(255,255,255,0.04)', color: c===cat ? '#0c0c0c' : '#8a8278', border: c===cat ? 'none' : '1px solid rgba(255,255,255,0.08)', fontFamily:'var(--font-sans)', fontWeight:500 }}>
                {c.replace(/-/g,' ')}
              </button>
            ))}
          </div>
          {loading ? <div style={{ textAlign:'center', padding:'4rem', color:'#8a8278' }}>Loading projects...</div> :
           filtered.length===0 ? (
            <div style={{ textAlign:'center', padding:'5rem' }}>
              <div style={{ fontSize:'3rem', marginBottom:'1rem' }}>🏠</div>
              <p style={{ color:'#8a8278' }}>No projects yet in this category. Check back soon!</p>
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'20px' }} className="port-grid">
              {filtered.map((p,i) => (
                <div key={p._id} className="port-item" style={{ height:'340px', cursor:'pointer' }} onClick={()=>setSelected(p)}>
                  {p.images?.[0] ? <img src={imgSrc(p.images[0])} alt={p.title} /> : <div style={{ width:'100%', height:'100%', background:'#1c1c1c', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'3rem' }}>🏠</div>}
                  <div className="port-overlay">
                    <span className="badge badge-gold" style={{ marginBottom:'0.5rem', alignSelf:'flex-start' }}>{p.category?.replace(/-/g,' ')}</span>
                    <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.15rem', fontWeight:400, color:'#f8f5ef' }}>{p.title}</h3>
                    {/* {p.location && <p style={{ color:'#8a8278', fontSize:'0.7rem', marginTop:'0.2rem' }}>📍 {p.location}</p>} */}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <style>{`@media(max-width:640px){.port-grid{grid-template-columns:1fr!important}}`}</style>
      </section>

      {/* Lightbox */}
      {selected && (
        <div onClick={()=>setSelected(null)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.96)', zIndex:9000, display:'flex', alignItems:'center', justifyContent:'center', padding:'1.5rem', overflowY:'auto' }}>
          <div onClick={e=>e.stopPropagation()} style={{ background:'#141414', border:'1px solid rgba(245,168,0,0.2)', borderRadius:'var(--radius)', maxWidth:'820px', width:'100%', maxHeight:'90vh', overflow:'auto' }}>
            {selected.images?.[0] && <img src={imgSrc(selected.images[0])} alt={selected.title} style={{ width:'100%', height:'420px', objectFit:'cover' }} />}
            <div style={{ padding:'2.5rem' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1rem' }}>
                <div>
                  <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.8rem', fontWeight:400, color:'#f8f5ef', marginBottom:'0.5rem' }}>{selected.title}</h2>
                  <span className="badge badge-gold">{selected.category?.replace(/-/g,' ')}</span>
                </div>
                <button onClick={()=>setSelected(null)} style={{ color:'#8a8278', fontSize:'1.3rem', cursor:'pointer', background:'none' }}>✕</button>
              </div>
              {selected.description && <p style={{ color:'#8a8278', lineHeight:'1.8', fontSize:'0.86rem', marginTop:'1rem' }}>{selected.description}</p>}
              <div style={{ display:'flex', gap:'0.75rem', flexWrap:'wrap', marginTop:'1rem' }}>
                {selected.bhkType && <span className="badge badge-muted">{selected.bhkType}</span>}
                {selected.location && <span className="badge badge-muted">📍 {selected.location}</span>}
                {selected.area && <span className="badge badge-muted">{selected.area}</span>}
              </div>
              {selected.images?.length > 1 && (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'4px', marginTop:'1.5rem' }}>
                  {selected.images.slice(1).map((img,i) => <img key={i} src={imgSrc(img)} alt="" style={{ width:'100%', height:'100px', objectFit:'cover' }} />)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// ─── OFFERS ────────────────────────────────────────────────────────────────
export function Offers() {
  const [offers, setOffers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [cat, setCat] = useState('all');
  const CATS = ['all','living-room','bedroom','kitchen','bathroom','false-ceiling','commercial','complete-modular'];

useEffect(() => {
  api.get('/offers/all')
    .then(r => {
      const d = Array.isArray(r.data)
        ? r.data
        : (r.data?.offers || r.data?.data || []);

      setOffers(d);
      setFiltered(d);
    })
    .catch(console.error)
    .finally(() => setLoading(false));
}, []);

useEffect(() => {
  setFiltered(
    cat === 'all'
      ? offers
      : offers.filter(o => o.category === cat)
  );
}, [cat, offers]);

  const isExpired = (validTill) => validTill && new Date(validTill) < new Date();

  return (
    <>
      <Helmet>
        <title>Offers | Wedoo Interiors — Current Deals & Discounts</title>
        <meta name="description" content="Check out the latest offers and discounts from Wedoo Interiors on modular kitchens, living rooms, false ceilings and complete interior packages in Tumakuru, Karnataka." />
      </Helmet>

      <div style={{ paddingTop:'5rem', paddingBottom:'5rem', background:'#141414', borderBottom:'1px solid rgba(245,168,0,0.12)' }}>
        <div className="container"><SectionHeader eyebrow="" title={<>Current <em style={{color:'#F5A800',fontStyle:'italic'}}>Offers</em></>} subtitle="" /></div>
      </div>

      <section className="section-pad" style={{ background:'#0c0c0c' }}>
        <div className="container">
          <div style={{ display:'flex', gap:'0.4rem', flexWrap:'wrap', marginBottom:'3rem' }}>
            {CATS.map(c => (
              <button key={c} onClick={()=>setCat(c)} style={{ padding:'0.5rem 1.25rem', fontSize:'0.68rem', letterSpacing:'0.1em', textTransform:'capitalize', cursor:'pointer', transition:'all 0.3s', background: c===cat ? '#F5A800' : 'rgba(255,255,255,0.04)', color: c===cat ? '#0c0c0c' : '#8a8278', border: c===cat ? 'none' : '1px solid rgba(255,255,255,0.08)', fontFamily:'var(--font-sans)', fontWeight:500 }}>
                {c.replace(/-/g,' ')}
              </button>
            ))}
          </div>
          
          {loading ? <div style={{ textAlign:'center', padding:'2rem', color:'#8a8278' }}>Loading offers...</div> :
           filtered.length===0 ? (
            <div style={{ textAlign:'center', padding:'5rem' }}>
              <div style={{ fontSize:'3rem', marginBottom:'1rem' }}>🎁</div>
              <p style={{ color:'#8a8278' }}>No active offers right now. Check back soon!</p>
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'1rem' }} className="offer-grid">
              {filtered.map((o) => {
                const expired = isExpired(o.validTill);
                return (
                  <div
                    key={o._id}
                    className="offer-ticket"
                    style={{ opacity: expired ? 0.45 : 1, cursor: expired ? 'default' : 'pointer' }}
                    onClick={()=>!expired && setSelected(o)}
                  >
                    {/* Image */}
                    <div style={{ position:'relative', width:'100%', height:'220px' }}>
                      {o.images?.[0]
                        ? <img src={imgSrc(o.images[0])} alt={o.title} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block', filter: expired ? 'grayscale(1)' : 'none' }} />
                        : <div style={{ width:'100%', height:'100%', background:'#1e1e1e', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'2.5rem' }}>🎁</div>
                      }
                      <span style={{
                        position:'absolute', top:'0.9rem', left:'0.9rem',
                        background:'#F5A800', color:'#0c0c0c',
                        fontSize:'0.66rem', fontWeight:700, letterSpacing:'0.08em',
                        padding:'0.3rem 0.7rem'
                      }}>
                        {(o.offerLabel || 'OFFER').toUpperCase()}
                      </span>
                      {expired && (
                        <span style={{
                          position:'absolute', top:'0.9rem', right:'0.9rem',
                          border:'1px solid #f87171', borderRadius:'var(--radius)', color:'#f87171',
                          fontSize:'0.62rem', fontWeight:700, letterSpacing:'0.08em',
                          padding:'0.25rem 0.6rem', transform:'rotate(4deg)'
                        }}>
                          EXPIRED
                        </span>
                      )}
                    </div>

                    {/* Ticket tear divider */}
                    <div className="ticket-tear">
                      <span className="notch notch-left" />
                      <span className="tear-line" />
                      <span className="notch notch-right" />
                    </div>

                    {/* Body */}
                    <div style={{ padding:'1.5rem 1.5rem 1.75rem', textAlign:'center' }}>
                      <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.3rem', fontWeight:600, color:'#f8f5ef', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'0.9rem' }}>
                        {o.title}
                      </h3>

                      <div style={{ display:'flex', alignItems:'baseline', justifyContent:'center', gap:'0.75rem', flexWrap:'wrap' }}>
                        {o.actualPrice != null && o.actualPrice !== '' && (
                          <span style={{ position:'relative', fontSize:'0.95rem', color:'#8a8278' }}>
                            ₹{o.actualPrice} {o.actualPriceSuffix}
                            <span style={{ position:'absolute', left:0, right:0, top:'50%', borderTop:'1.5px solid #e11d48', transform:'rotate(-8deg)' }} />
                          </span>
                        )}
                        <span style={{ fontFamily:'var(--font-display)', fontSize:'2rem', fontWeight:700, color:'#F5A800' }}>
                          ₹{o.offerPrice} <span style={{ fontSize:'1.1rem' }}>{o.offerPriceSuffix}</span>
                        </span>
                      </div>

                      {o.description && (
                        <p style={{ marginTop:'0.85rem', fontSize:'0.78rem', color:'#8a8278', lineHeight:1.6, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
                          {o.description}
                        </p>
                      )}

                      <div style={{ marginTop:'1.25rem', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.75rem' }}>
                        <span style={{ fontSize:'0.68rem', color:'#5c564d', letterSpacing:'0.03em' }}>
                          {o.validTill ? (expired ? 'Offer expired' : `Valid till ${new Date(o.validTill).toLocaleDateString()}`) : ''}
                        </span>
                        {!expired && (
                          <span style={{ fontSize:'0.68rem', fontWeight:600, letterSpacing:'0.08em', color:'#F5A800', border:'1px solid rgba(245,168,0,0.4)', borderRadius:'var(--radius)', padding:'0.4rem 0.85rem' }}>
                            VIEW DETAILS
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <style>{`
          .offer-ticket {
            position: relative;
            background: #141414;
            border: 1px solid rgba(245,168,0,0.15);
            overflow: hidden;
            transition: border-color 0.25s ease, transform 0.25s ease;
          }
          .offer-ticket:hover {
            border-color: rgba(245,168,0,0.5);
            transform: translateY(-3px);
          }
          .ticket-tear {
            position: relative;
            height: 1px;
            background-image: repeating-linear-gradient(to right, rgba(245,168,0,0.35) 0 8px, transparent 8px 16px);
          }
          .ticket-tear .notch {
            position: absolute;
            top: 50%; transform: translateY(-50%);
            width: 20px; height: 20px;
            background: #0c0c0c;
            border-radius: 50%;
          }
          .notch-left { left: -10px; }
          .notch-right { right: -10px; }
          @media(max-width:900px){ .offer-grid{ grid-template-columns:repeat(2,1fr)!important; } }
          @media(max-width:640px){ .offer-grid{ grid-template-columns:1fr!important; } }
        `}</style>
      </section>

      {/* Lightbox */}
      {selected && (
        <div
          onClick={() => setSelected(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)',
            zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1.5rem', overflowY: 'auto'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#141414', border:'1px solid rgba(245,168,0,0.2)', borderRadius:'var(--radius)',
              maxWidth: '820px', width: '100%',
              maxHeight: '90vh', overflow: 'auto'
            }}
          >
            {/* Hero image */}
            <div style={{ position: 'relative' }}>
              {selected.images?.[0] ? (
                <img
                  src={imgSrc(selected.images[0])}
                  alt={selected.title}
                  style={{ width: '100%', height: '340px', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <div style={{ width: '100%', height: '340px', background: '#1e1e1e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem' }}>🎁</div>
              )}
              <span style={{
                position:'absolute', top:'1.1rem', left:'1.1rem',
                background:'#F5A800', color:'#0c0c0c',
                fontSize:'0.68rem', fontWeight:700, letterSpacing:'0.08em',
                padding:'0.35rem 0.8rem'
              }}>
                {(selected.offerLabel || 'OFFER').toUpperCase()}
              </span>
              <button
                onClick={() => setSelected(null)}
                style={{
                  position: 'absolute', top: '0.85rem', right: '0.85rem',
                  width: '2.2rem', height: '2.2rem', borderRadius: '50%',
                  background: 'rgba(0,0,0,0.6)', color: '#F5A800', fontSize: '1.1rem',
                  border: '1px solid rgba(245,168,0,0.3)', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>

            {/* Details */}
            <div style={{ padding: '2rem 2.5rem 2.5rem' }}>
              <h2 style={{ fontFamily:'var(--font-display)', fontSize: '1.8rem', fontWeight: 600, color: '#f8f5ef', textTransform:'uppercase', letterSpacing:'0.03em', marginBottom: '0.5rem' }}>
                {selected.title}
              </h2>

              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.9rem', flexWrap: 'wrap', marginTop: '0.9rem' }}>
                {selected.actualPrice != null && selected.actualPrice !== '' && (
                  <span style={{ position: 'relative', fontSize: '1.05rem', color: '#8a8278' }}>
                    ₹{selected.actualPrice} {selected.actualPriceSuffix}
                    <span style={{ position: 'absolute', left: 0, right: 0, top: '50%', borderTop: '1.5px solid #e11d48', transform: 'rotate(-8deg)' }} />
                  </span>
                )}
                <span style={{ fontFamily:'var(--font-display)', fontSize: '2.2rem', fontWeight: 700, color: '#F5A800' }}>
                  ₹{selected.offerPrice} <span style={{ fontSize:'1.2rem' }}>{selected.offerPriceSuffix}</span>
                </span>
              </div>

              <p style={{ color: '#a8a196', lineHeight: '1.8', fontSize: '0.92rem', marginTop: '1.5rem' }}>
                {selected.description || 'Full package details will be shared by our design consultant during your free consultation.'}
              </p>

              {selected.validTill && (
                <div style={{ marginTop:'1.5rem' }}>
                  <span style={{ fontSize: '0.75rem', color: '#8a8278', border: '1px solid rgba(245,168,0,0.25)', borderRadius:'var(--radius)', padding: '0.4rem 0.9rem' }}>
                    {new Date(selected.validTill) < new Date()
                      ? 'Expired'
                      : `Valid till ${new Date(selected.validTill).toLocaleDateString()}`}
                  </span>
                </div>
              )}

              {selected.images?.length > 1 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '6px', marginTop: '1.75rem' }}>
                  {selected.images.slice(1).map((img, i) => (
                    <img
                      key={i}
                      src={imgSrc(img)}
                      alt=""
                      style={{ width: '100%', height: '100px', objectFit: 'cover' }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// ─── CAREERS ─────────────────────────────────────────────────────────────────
export function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyJob, setApplyJob] = useState(null);
  const { register, handleSubmit, reset, formState:{errors} } = useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get('/careers').then(r=>{ const d = Array.isArray(r.data)?r.data:(r.data?.careers||r.data?.data||[]); setJobs(d); }).catch(()=>{}).finally(()=>setLoading(false)); }, []);

  const onApply = async (data) => {
    if (!applyJob) return undefined;
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(data).forEach(([k,v]) => v && fd.append(k, v));
      if (data.resume?.[0]) fd.append('resume', data.resume[0]);
      await api.post(`/careers/${applyJob._id}/apply`, fd, { headers:{'Content-Type':'multipart/form-data'} });
      toast.success('Application submitted! We will be in touch soon.');
      reset(); setApplyJob(null);
    } catch { toast.error('Failed to submit. Please try again.'); }
    finally { setSubmitting(false); }
  };

  return (
    <>
      <Helmet>
        <title>Careers at Wedoo Interiors | Jobs in Tumakuru</title>
        <meta name="description" content="Join Wedoo Interiors — Tumakuru's premier interior design studio. View current job openings and apply online." />
      </Helmet>
      <div style={{ paddingTop:'7rem', paddingBottom:'5rem', background:'#141414', borderBottom:'1px solid rgba(245,168,0,0.12)' }}>
        <div className="container"><SectionHeader eyebrow="Join Our Team" title={<>Careers at <em style={{color:'#F5A800',fontStyle:'italic'}}>Wedoo</em></>} subtitle="We're always looking for passionate, talented people who love creating extraordinary spaces." /></div>
      </div>
      <section className="section-pad" style={{ background:'#0c0c0c' }}>
        <div className="container">
          {loading ? <div style={{ textAlign:'center', padding:'3rem', color:'#8a8278' }}>Loading...</div> :
           jobs.length === 0 ? (
            <div style={{ textAlign:'center', padding:'5rem', maxWidth:'500px', margin:'0 auto' }}>
              <div style={{ fontSize:'3rem', marginBottom:'1.5rem' }}>🔍</div>
              <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.8rem', fontWeight:400, marginBottom:'1rem', color:'#f8f5ef' }}>No Open Positions Right Now</h3>
              <p style={{ color:'#8a8278', marginBottom:'2rem', lineHeight:'1.8', fontSize:'0.88rem' }}>We don't have any openings at the moment, but we're always growing. Drop us your portfolio and resume!</p>
              <a href="mailto:hello@wedoointeriors.com" className="btn btn-gold">Send Your CV</a>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'2px', background:'rgba(245,168,0,0.04)' }}>
              {jobs.map(j => (
                <div key={j._id} style={{ background:'#0c0c0c', padding:'2.5rem', borderBottom:'1px solid rgba(255,255,255,0.04)', transition:'background 0.3s' }}
                  onMouseEnter={e=>e.currentTarget.style.background='#141414'}
                  onMouseLeave={e=>e.currentTarget.style.background='#0c0c0c'}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:'1.5rem' }}>
                    <div>
                      <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.5rem', fontWeight:400, color:'#f8f5ef', marginBottom:'0.75rem' }}>{j.title}</h3>
                      <div style={{ display:'flex', gap:'0.4rem', flexWrap:'wrap' }}>
                        {[j.department,j.type,j.location,j.experience].filter(Boolean).map(t=><span key={t} className="badge badge-gold">{t}</span>)}
                        {j.salary && <span className="badge badge-green">{j.salary}</span>}
                      </div>
                    </div>
                    <button className="btn btn-gold" onClick={()=>setApplyJob(j)} style={{ flexShrink:0, fontSize:'0.7rem' }}>Apply Now →</button>
                  </div>
                  {j.description && <p style={{ color:'#8a8278', fontSize:'0.84rem', lineHeight:'1.8', marginTop:'1.25rem' }}>{j.description}</p>}
                  {j.requirements?.length > 0 && (
                    <div style={{ marginTop:'1rem', display:'flex', flexWrap:'wrap', gap:'0.5rem' }}>
                      {j.requirements.map(r=><span key={r} style={{ fontSize:'0.75rem', color:'#8a8278' }}>✦ {r}</span>)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Apply modal */}
      {applyJob && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.92)', zIndex:9000, display:'flex', alignItems:'center', justifyContent:'center', padding:'1.5rem', overflowY:'auto' }}>
          <div style={{ background:'#141414', border:'1px solid rgba(245,168,0,0.2)', borderRadius:'var(--radius)', maxWidth:'600px', width:'100%', padding:'2.5rem', margin:'auto', maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'2rem' }}>
              <div>
                <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.5rem', fontWeight:400, color:'#f8f5ef' }}>Apply: {applyJob.title}</h3>
                <p style={{ color:'#8a8278', fontSize:'0.75rem', marginTop:'0.25rem' }}>Fill in your details below</p>
              </div>
              <button onClick={()=>{setApplyJob(null);reset();}} style={{ color:'#8a8278', fontSize:'1.3rem', cursor:'pointer' }}>✕</button>
            </div>
            <form onSubmit={handleSubmit(onApply)}>
              <div className="grid-2">
                <div className="form-group"><label>Full Name *</label><input {...register('name',{required:true})} placeholder="Your full name" />{errors.name&&<p className="form-error">Required</p>}</div>
                <div className="form-group"><label>Email *</label><input type="email" {...register('email',{required:true})} placeholder="your@email.com" /></div>
              </div>
              <div className="grid-2">
                <div className="form-group"><label>Phone *</label><input {...phoneField(register,'phone',true)} />{errors.phone&&<p className="form-error">{errors.phone.message||'Required'}</p>}</div>
                <div className="form-group"><label>Experience</label><input {...register('experience')} placeholder="e.g. 3 years" /></div>
              </div>
              <div className="form-group"><label>Current Company</label><input {...register('currentCompany')} placeholder="Where do you work now?" /></div>
              <div className="form-group"><label>Portfolio / LinkedIn URL</label><input {...register('portfolio')} placeholder="https://..." /></div>
              <div className="form-group"><label>Cover Letter</label><textarea {...register('coverLetter')} rows={4} placeholder="Why are you the perfect fit?" /></div>
              <div className="form-group"><label>Resume (PDF/DOC)</label><input type="file" {...register('resume')} accept=".pdf,.doc,.docx" style={{ color:'#8a8278', padding:'0.5rem 0' }} /></div>
              <button type="submit" className="btn btn-gold" style={{ width:'100%', justifyContent:'center' }} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Application →'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

// ─── CONTACT ─────────────────────────────────────────────────────────────────
export function Contact() {
  const { register, handleSubmit, reset, formState:{errors} } = useForm();
  const [loading, setLoading] = useState(false);

  const onSubmit = async (data) => {
    setLoading(true);
    try { await api.post('/contacts', data); toast.success('Message sent! We\'ll reply within 24 hours.'); reset(); }
    catch { toast.error('Failed to send. Please try again.'); }
    finally { setLoading(false); }
  };

  return (
    <>
      <Helmet>
        <title>Contact Wedoo Interiors | Tumakuru, Karnataka</title>
        <meta name="description" content="Contact Wedoo Interiors in Tumakuru, Karnataka. Visit us near Muneshwara Temple, Gokula Extension. Call +919686583195 for a free consultation." />
      </Helmet>
      <div style={{ paddingTop:'7rem', paddingBottom:'5rem', background:'#141414', borderBottom:'1px solid rgba(245,168,0,0.12)' }}>
        <div className="container"><SectionHeader eyebrow="Get In Touch" title={<>Let's <em style={{color:'#F5A800',fontStyle:'italic'}}>Connect</em></>} subtitle="We'd love to hear about your project. Our team responds within 24 hours." /></div>
      </div>
      <section className="section-pad" style={{ background:'#0c0c0c' }}>
        <div className="container">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1.6fr', gap:'5rem', alignItems:'start' }} className="contact-grid">
            <div>
              <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.6rem', fontWeight:400, color:'#f8f5ef', marginBottom:'2rem' }}>Studio Address</h3>
              <div style={{ display:'flex', flexDirection:'column', gap:'1.5rem', marginBottom:'2.5rem' }}>
                {[
                  { icon:'📍', label:'Address', val:'#1, Near Muneshwara Temple, Manjunath Nagar, Gokula Extension, Tumakuru, Karnataka 572104' },
                  { icon:'📞', label:'Phone', val:'+919686583195', href:'tel:+919686583195' },
                  { icon:'💬', label:'WhatsApp', val:'Chat with us', href:'https://api.whatsapp.com/send?phone=919686583195&text=Hi%20Wedoo%20Interiors' },
                  { icon:'📧', label:'Email', val:'hello@wedoointeriors.com', href:'mailto:hello@wedoointeriors.com' },
                  { icon:'🕒', label:'Hours', val:'Mon – Sat: 9:00 AM – 7:00 PM' },
                ].map(i => (
                  <div key={i.label} style={{ display:'flex', gap:'1rem' }}>
                    <div style={{ width:'42px', height:'42px', border:'1px solid rgba(245,168,0,0.25)', borderRadius:'var(--radius)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1rem', flexShrink:0 }}>{i.icon}</div>
                    <div>
                      <div style={{ fontSize:'0.62rem', letterSpacing:'0.16em', textTransform:'uppercase', color:'#F5A800', marginBottom:'0.25rem' }}>{i.label}</div>
                      {i.href ? <a href={i.href} style={{ color:'#ede8de', fontSize:'0.85rem', lineHeight:'1.6' }}>{i.val}</a> : <p style={{ color:'#8a8278', fontSize:'0.85rem', lineHeight:'1.6' }}>{i.val}</p>}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ border:'1px solid rgba(245,168,0,0.15)', borderRadius:'var(--radius)', overflow:'hidden' }}>
                <iframe title="Wedoo Interiors Location" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15552.64!2d77.1173!3d13.3379!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTPCsDIwJzE2LjQiTiA3N8KwMDcnMDIuMyJF!5e0!3m2!1sen!2sin!4v1" width="100%" height="220" style={{ border:0, display:'block', filter:'grayscale(0.4)' }} loading="lazy" />
              </div>
            </div>
            <div style={{ background:'#141414', border:'1px solid rgba(245,168,0,0.12)', borderRadius:'var(--radius)', padding:'3rem' }}>
              <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.6rem', fontWeight:400, color:'#f8f5ef', marginBottom:'2rem' }}>Send a Message</h3>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="grid-2">
                  <div className="form-group"><label>Full Name *</label><input {...register('name',{required:true})} placeholder="Your name" />{errors.name&&<p className="form-error">Required</p>}</div>
                  <div className="form-group"><label>Email *</label><input type="email" {...register('email',{required:true})} placeholder="your@email.com" /></div>
                </div>
                <div className="form-group"><label>Phone</label><input {...phoneField(register,'phone',false)} />{errors.phone&&<p className="form-error">{errors.phone.message}</p>}</div>
                <div className="form-group"><label>Subject</label><input {...register('subject')} placeholder="How can we help?" /></div>
                <div className="form-group"><label>Message *</label><textarea {...register('message',{required:true})} rows={5} placeholder="Tell us about your project..." />{errors.message&&<p className="form-error">Required</p>}</div>
                <button type="submit" className="btn btn-gold" style={{ width:'100%', justifyContent:'center' }} disabled={loading}>
                  {loading ? 'Sending...' : 'Send Message →'}
                </button>
              </form>
            </div>
          </div>
        </div>
        <style>{`@media(max-width:860px){.contact-grid{grid-template-columns:1fr!important;gap:3rem!important}}`}</style>
      </section>
    </>
  );
}

// ─── GET QUOTE ────────────────────────────────────────────────────────────────
export function GetQuote() {
  const { register, handleSubmit, reset, formState:{errors} } = useForm();
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const SVCS = ['Complete Modular Works','Interior Design','Home Lighting','Modular Kitchen','False Ceiling & Painting','Aluminium & Glass Works'];
  const BHKS = ['1 BHK','2 BHK','3 BHK','4+ BHK / Duplex','Independent Home / Villa','Commercial Space'];
  const CITIES = ['Tumakuru','Bengaluru','Bijapur','Dharwad','Raichur','Bellary','Belgaum','Bagalkot'];
  const BUDGETS = ['Under ₹5 Lakhs','₹5–10 Lakhs','₹10–20 Lakhs','₹20–50 Lakhs','₹50 Lakhs+'];
  const toggle = (s) => setServices(p => p.includes(s) ? p.filter(x=>x!==s) : [...p,s]);

  const onSubmit = async (data) => {
    if (!services.length) { toast.error('Select at least one service'); return undefined; }
    setLoading(true);
    try { await api.post('/quotes', {...data, services}); toast.success('Quote request received! We\'ll contact you within 2 hours.'); reset(); setServices([]); }
    catch { toast.error('Failed to submit. Please try again.'); }
    finally { setLoading(false); }
  };

  return (
    <>
      <Helmet>
        <title>Get Free Quote | Wedoo Interiors Tumakuru</title>
        <meta name="description" content="Get a free interior design quote from Wedoo Interiors. Fill in your details and our experts will contact you within 2 hours." />
      </Helmet>
      <div style={{ paddingTop:'7rem', paddingBottom:'5rem', background:'#141414', borderBottom:'1px solid rgba(245,168,0,0.12)' }}>
        <div className="container"><SectionHeader eyebrow="Free Consultation" title={<>Get Your <em style={{color:'#F5A800',fontStyle:'italic'}}>Free Quote</em></>} subtitle="Fill in the details below and our design experts will reach out within 2 hours with a tailored estimate." /></div>
      </div>
      <section className="section-pad" style={{ background:'#0c0c0c' }}>
        <div className="container" style={{ maxWidth:'800px' }}>
          <form onSubmit={handleSubmit(onSubmit)} className="getquote-form" style={{ background:'#141414', border:'1px solid rgba(245,168,0,0.12)', borderRadius:'var(--radius)', padding:'3rem' }}>
            <div className="grid-2">
              <div className="form-group"><label>Full Name *</label><input {...register('name',{required:true})} placeholder="Your name" />{errors.name&&<p className="form-error">Required</p>}</div>
              <div className="form-group"><label>Phone Number *</label><input {...phoneField(register,'phone',true)} />{errors.phone&&<p className="form-error">{errors.phone.message||'Required'}</p>}</div>
            </div>
            <div className="form-group"><label>Email Address *</label><input type="email" {...register('email',{required:true})} placeholder="your@email.com" /></div>
            <div className="grid-2">
              <div className="form-group"><label>Property Type</label><select {...register('bhkType')}><option value="">Select type</option>{BHKS.map(b=><option key={b} value={b}>{b}</option>)}</select></div>
              <div className="form-group"><label>City</label><select {...register('city')}><option value="">Select city</option>{CITIES.map(c=><option key={c} value={c}>{c}</option>)}</select></div>
            </div>
            <div className="form-group"><label>Budget Range</label><select {...register('budget')}><option value="">Select budget</option>{BUDGETS.map(b=><option key={b} value={b}>{b}</option>)}</select></div>
            <div className="form-group">
              <label>Services Required *</label>
              <div className="svc-check-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.5rem', marginTop:'0.5rem' }}>
                {SVCS.map(s => (
                  <button type="button" key={s} onClick={()=>toggle(s)} style={{ padding:'0.8rem 1rem', fontSize:'0.76rem', textAlign:'left', cursor:'pointer', transition:'all 0.3s', fontFamily:'var(--font-sans)', background: services.includes(s) ? 'rgba(245,168,0,0.12)' : 'rgba(255,255,255,0.03)', border: services.includes(s) ? '1px solid #F5A800' : '1px solid rgba(255,255,255,0.08)', color: services.includes(s) ? '#F5A800' : '#8a8278' }}>
                    {services.includes(s) ? '✓ ' : ''}{s}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group"><label>Additional Details</label><textarea {...register('message')} rows={4} placeholder="Timeline, special requirements, or anything else..." /></div>
            <button type="submit" className="btn btn-gold" style={{ width:'100%', justifyContent:'center', fontSize:'0.75rem' }} disabled={loading}>
              {loading ? 'Submitting...' : '✦ Submit Quote Request'}
            </button>
            <p style={{ textAlign:'center', color:'#8a8278', fontSize:'0.72rem', marginTop:'1rem' }}>We respond within 2 hours during working hours (Mon–Sat, 9am–7pm)</p>
          </form>
          <style>{`
            @media (max-width: 480px) {
              .getquote-form { padding: 1.75rem 1.25rem !important; }
              .svc-check-grid { grid-template-columns: 1fr !important; }
            }
          `}</style>
        </div>
      </section>
    </>
  );
}
